/**
 * 계약 연장 모달 (V45)
 *
 * 계약 종료일을 연장하는 모달입니다.
 */

import React, { useState, useEffect } from 'react';
import { X, Calendar, AlertCircle, CheckCircle } from 'lucide-react';
import { useModalOverlayClose } from '../../../../shared/hooks/useModalOverlayClose';
import { EmploymentManagementItem } from '../../../../shared/hooks/useEmploymentManagement';

interface ContractExtensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (newEndDate: string) => Promise<void>;
  item: EmploymentManagementItem | null;
  isLoading: boolean;
}

export function ContractExtensionModal({
  isOpen,
  onClose,
  onSubmit,
  item,
  isLoading,
}: ContractExtensionModalProps) {
  const overlayProps = useModalOverlayClose(onClose);
  const [newEndDate, setNewEndDate] = useState('');
  const [error, setError] = useState('');

  // 모달 열릴 때 초기화
  useEffect(() => {
    if (isOpen && item) {
      // 기본값: 현재 종료일 + 1년
      const defaultDate = getDefaultExtensionDate(item.endDate);
      setNewEndDate(defaultDate);
      setError('');
    }
  }, [isOpen, item]);

  // 기본 연장일 계산 (현재 종료일 + 1년, 없으면 오늘 + 1년)
  const getDefaultExtensionDate = (currentEndDate?: string) => {
    const baseDate = currentEndDate ? new Date(currentEndDate) : new Date();
    baseDate.setFullYear(baseDate.getFullYear() + 1);
    return baseDate.toISOString().split('T')[0];
  };

  // 최소 선택 가능 날짜 (현재 종료일 + 1일 또는 오늘)
  const getMinDate = () => {
    if (item?.endDate) {
      const nextDay = new Date(item.endDate);
      nextDay.setDate(nextDay.getDate() + 1);
      return nextDay.toISOString().split('T')[0];
    }
    return new Date().toISOString().split('T')[0];
  };

  // 제출 처리
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 유효성 검증
    if (!newEndDate) {
      setError('새 계약 종료일을 선택해주세요.');
      return;
    }

    const minDate = getMinDate();
    if (newEndDate < minDate) {
      setError(`새 종료일은 현재 종료일(${item?.endDate}) 이후여야 합니다.`);
      return;
    }

    try {
      await onSubmit(newEndDate);
    } catch (err: any) {
      setError(err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  if (!isOpen || !item) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      {...overlayProps}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>계약 연장</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 본문 */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px' }}>
            {/* 직원 정보 */}
            <div
              style={{
                background: 'var(--color-gray-50)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
              }}
            >
              <div style={{ fontSize: '14px', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
                대상 직원
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600 }}>{item.employeeName}</div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                {item.orgUnitName} · {item.employmentTypeName}
              </div>
              {item.endDate && (
                <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginTop: '2px' }}>
                  현재 계약 종료일: {item.endDate}
                </div>
              )}
            </div>

            {/* 퇴사 예약 경고 */}
            {item.terminationPlanned && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#92400e',
                }}
              >
                <AlertCircle size={16} />
                현재 퇴사 예약({item.terminationPlannedEndDate})이 있습니다. 계약 연장 시 자동 취소됩니다.
              </div>
            )}

            {/* 새 계약 종료일 */}
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                }}
              >
                새 계약 종료일 *
              </label>
              <div style={{ position: 'relative' }}>
                <Calendar
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--color-gray-400)',
                  }}
                />
                <input
                  type="date"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  min={getMinDate()}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 40px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#dc2626',
                  fontSize: '13px',
                }}
              >
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* 안내 메시지 */}
            <div
              style={{
                marginTop: '16px',
                padding: '12px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#166534',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
              }}
            >
              <CheckCircle size={16} style={{ marginTop: '1px', flexShrink: 0 }} />
              <div>계약이 연장되면 고용 이력의 종료일이 업데이트됩니다.</div>
            </div>
          </div>

          {/* 푸터 */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--color-gray-200)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                background: 'white',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              {isLoading ? '처리 중...' : '계약 연장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
